import React, { useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, NumberInput, SelectPicker, Toggle, Message } from 'rsuite';
import { AxiosError } from 'axios';
import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel } from '@/models/core.model';
import {
  AttendeeCondition, AttendeeConditionList, PointEarnPeriodList, PointEarnRule, PointEarnTrigger, PointEarnTriggerList,
  UpsertPointEarnRule, VisitCondition, VisitConditionList, getRuleAttendee, getRuleVisit, isEnergyHubTrigger,
} from '@/models/point-account.model';
import { createPointEarnRule, updatePointEarnRule } from '@/services/point-account.service';
import { toastSuccessMessage } from '@/utils/toasterMessage';

interface RuleFormValue {
  name: string;
  trigger: PointEarnTrigger;
  attendee: AttendeeCondition;
  visit: VisitCondition;
  amount?: number;
  expiresAfterDays?: number;
  priority?: number;
  maxPerUser?: number;
  maxPerUserPerPeriod?: number;
  period?: number;
  isActive: boolean;
}

const modelSchema = Schema.Model<Record<string, any>>({
  name: Schema.Types.StringType().isRequired('This field is required.').maxLength(200),
  trigger: Schema.Types.NumberType().isRequired('This field is required.'),
  attendee: Schema.Types.StringType()
    .addRule((value, data) => data.trigger != PointEarnTrigger.ClubEventCheckIn || !!value, 'This field is required.', true),
  amount: Schema.Types.NumberType().isRequired('This field is required.').min(1, 'Amount must be at least 1.'),
  expiresAfterDays: Schema.Types.NumberType().range(1, 3650, 'Between 1 and 3650 days.'),
  priority: Schema.Types.NumberType(),
  maxPerUser: Schema.Types.NumberType().min(1, 'Must be at least 1.'),
  maxPerUserPerPeriod: Schema.Types.NumberType().min(1, 'Must be at least 1.')
    .addRule((value, data) => !(value != null && value !== '' && !data.period) , 'Pick a period too.'),
  period: Schema.Types.NumberType()
    .addRule((value, data) => !(value != null && (data.maxPerUserPerPeriod == null || data.maxPerUserPerPeriod === '')), 'Set max per user per period too.'),
});

const emptyModel: RuleFormValue = {
  name: '',
  trigger: PointEarnTrigger.ClubEventCheckIn,
  attendee: 'Any',
  visit: 'Any',
  amount: undefined,
  expiresAfterDays: undefined,
  priority: 0,
  maxPerUser: undefined,
  maxPerUserPerPeriod: undefined,
  period: undefined,
  isActive: true,
};

const num = (v: any): number | undefined => (v === null || v === undefined || v === '' ? undefined : Number(v));

interface Props extends DrawerProps {
  pointAccountId: number;
  rule?: PointEarnRule;
  onSubmitted?: () => void;
}

const PointEarnRuleDrawer = (props: Props) => {
  const { open, onClose, onSubmitted, pointAccountId, rule, ...rest } = props;
  const [formValue, setFormValue] = useState<RuleFormValue>({ ...emptyModel });
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  React.useEffect(() => {
    if (!open) return;
    setErrorModel(undefined);
    setFormValue(rule ? {
      name: rule.name,
      trigger: rule.trigger,
      attendee: getRuleAttendee(rule),
      visit: getRuleVisit(rule),
      amount: rule.amount,
      expiresAfterDays: rule.expiresAfterDays ?? undefined,
      priority: rule.priority,
      maxPerUser: rule.maxPerUser ?? undefined,
      maxPerUserPerPeriod: rule.maxPerUserPerPeriod ?? undefined,
      period: rule.period ?? undefined,
      isActive: rule.isActive,
    } : { ...emptyModel });
  }, [open, rule]);

  const handleClose = (event?: React.SyntheticEvent) => {
    setLoading(false);
    onClose?.(event as any);
  };

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) return;
    // Conditions are parsed server-side with a string-only enum converter: send the name, not the number.
    const body: UpsertPointEarnRule = {
      name: formValue.name,
      trigger: formValue.trigger,
      conditions: formValue.trigger == PointEarnTrigger.ClubEventCheckIn
        ? { attendee: formValue.attendee }
        : isEnergyHubTrigger(formValue.trigger) ? { visit: formValue.visit } : {},
      amount: num(formValue.amount),
      expiresAfterDays: num(formValue.expiresAfterDays),
      priority: num(formValue.priority) ?? 0,
      maxPerUser: num(formValue.maxPerUser),
      maxPerUserPerPeriod: num(formValue.maxPerUserPerPeriod),
      period: num(formValue.period),
      isActive: formValue.isActive,
    };
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result = rule
        ? await updatePointEarnRule(pointAccountId, rule.id, body)
        : await createPointEarnRule(pointAccountId, body);
      if (result.success) {
        toastSuccessMessage('Earn rule saved');
        handleClose();
        onSubmitted?.();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right" open={open} onClose={handleClose} {...rest}>
      <Drawer.Header>
        <Drawer.Title>{rule ? 'Edit Earn Rule' : 'New Earn Rule'}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">Save</Button>
          <Button onClick={handleClose} appearance="subtle">Cancel</Button>
        </Drawer.Actions>
      </Drawer.Header>
      <Drawer.Body>
        <Form name="point-earn-rule-form" fluid ref={formRef}
          onChange={value => setFormValue(pre => ({ ...pre, ...value } as RuleFormValue))}
          formValue={formValue} model={modelSchema} disabled={loading}
        >
          <Form.Group>
            <Form.Label>Name</Form.Label>
            <Form.Control name="name" />
          </Form.Group>
          <Form.Group>
            <Form.Label>Trigger</Form.Label>
            <Form.Control name="trigger" accepter={SelectPicker} data={PointEarnTriggerList} searchable={false} cleanable={false} block />
          </Form.Group>
          {formValue.trigger == PointEarnTrigger.ClubEventCheckIn && (
            <Form.Group>
              <Form.Label>Attendee condition</Form.Label>
              <Form.Control name="attendee" accepter={SelectPicker} data={AttendeeConditionList} searchable={false} cleanable={false} block />
              <Form.HelpText>Newcomer = no earlier approved check-in at any event of this club.</Form.HelpText>
            </Form.Group>
          )}
          {isEnergyHubTrigger(formValue.trigger) && (
            <>
              <Form.Group>
                <Form.Label>Visit condition</Form.Label>
                <Form.Control name="visit" accepter={SelectPicker} data={VisitConditionList} searchable={false} cleanable={false} block />
              </Form.Group>
              <Message type="info" showIcon style={{ marginBottom: 24 }}>
                {formValue.trigger == PointEarnTrigger.EnergyHubCheckIn
                  ? 'Pays when a runner checks in at a hub, solo or on an approved Hub Run. Pick "On a Hub Run (club)" to pay club check-ins only.'
                  : 'Pays when staff approve a runner\'s receipt (solo or club).'}
                {' '}Assign the account to Platform for all hubs, or to one Energy Hub.
              </Message>
            </>
          )}
          {formValue.trigger == PointEarnTrigger.UserSignUp && (
            <Message type="info" showIcon style={{ marginBottom: 24 }}>
              Pays once per user when they sign up (email sign-ups once verified). Only users who joined inside the
              account's start/end window earn it. The account needs a Platform assignment.
            </Message>
          )}
          <Form.Group>
            <Form.Label>Amount</Form.Label>
            <Form.Control name="amount" accepter={NumberInput} min={1} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Expires after days (optional)</Form.Label>
            <Form.Control name="expiresAfterDays" accepter={NumberInput} min={1} max={3650} />
            <Form.HelpText>Empty = granted points never expire.</Form.HelpText>
          </Form.Group>
          <Form.Group>
            <Form.Label>Priority</Form.Label>
            <Form.Control name="priority" accepter={NumberInput} />
            <Form.HelpText>Higher is evaluated first; the first passing rule of an account wins.</Form.HelpText>
          </Form.Group>
          <Form.Group>
            <Form.Label>Max per user (optional, lifetime)</Form.Label>
            <Form.Control name="maxPerUser" accepter={NumberInput} min={1} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Max per user per period (optional)</Form.Label>
            <Form.Control name="maxPerUserPerPeriod" accepter={NumberInput} min={1} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Period</Form.Label>
            <Form.Control name="period" accepter={SelectPicker} data={PointEarnPeriodList} searchable={false} block />
            <Form.HelpText>Required together with "max per user per period".</Form.HelpText>
          </Form.Group>
          <Form.Group>
            <Form.Label>Active</Form.Label>
            <Form.Control name="isActive" accepter={Toggle} />
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }} />}
      </Drawer.Body>
    </Drawer>
  );
};

export default PointEarnRuleDrawer;
